// Events — Supabase implementation.

import { supabase } from '@/lib/supabase';
import { mapEvent, toIso } from './mappers';
import type { AppEvent, EventStatus } from '@/types/domain';

/**
 * Archived and voided events are excluded unless asked for by name. That is the
 * entire point of those states: the list stopped being usable because every
 * test event and mistyped duplicate stayed in it forever. Pass an explicit
 * status to see them, or `includeHidden` to see everything.
 */
export async function listEvents(
  filters?: EventStatus | { status?: EventStatus; includeHidden?: boolean },
): Promise<AppEvent[]> {
  const status = typeof filters === 'string' ? filters : filters?.status;
  const includeHidden =
    typeof filters === 'string' ? false : (filters?.includeHidden ?? false);

  let q = supabase.from('events').select('*').order('start_at', { ascending: false });
  if (status) q = q.eq('status', status);
  else if (!includeHidden) q = q.in('status', ['active', 'closed']);

  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapEvent);
}

export async function getEvent(id: string): Promise<AppEvent | null> {
  const { data, error } = await supabase
    .from('events').select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapEvent(data) : null;
}

/**
 * Free-code check for the create form. Advisory: the real guarantee is the
 * UNIQUE index on events.code, which is what actually rejects a duplicate if
 * two people submit the same code at once.
 */
export async function isEventCodeAvailable(
  code: string,
  excludeId?: string,
): Promise<boolean> {
  let q = supabase.from('events').select('id', { count: 'exact', head: true }).eq('code', code);
  // When editing an event, its own row must not count as a collision.
  if (excludeId) q = q.neq('id', excludeId);
  const { count, error } = await q;
  if (error) throw new Error(error.message);
  return (count ?? 0) === 0;
}

const CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** Suggest an unused-looking event code, e.g. EVT-20260802-K7QF. */
export const generateEventCode = (ms: number = Date.now()): string => {
  const d = new Date(ms);
  const datePart = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(
    d.getDate(),
  ).padStart(2, '0')}`;
  let rand = '';
  for (let i = 0; i < 4; i += 1) {
    rand += CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)];
  }
  return `EVT-${datePart}-${rand}`;
};

export interface CreateEventInput {
  name: string;
  /** Defaults to a generated EVT-<date>-<rand> code. */
  code?: string;
  location?: string;
  /** Palette token, see lib/eventColors.ts. */
  color?: string | null;
  /** Defaults to now. */
  startAt?: number;
  endAt?: number;
  /**
   * Accepted but ignored. The actor is taken from auth.uid() server-side —
   * a client-supplied identity is not something the server should trust.
   * Kept in the type so existing call sites compile unchanged.
   */
  createdBy?: string;
}

/** Returns the new event's id, matching the previous contract. */
export async function createEvent(input: CreateEventInput): Promise<string> {
  const { data: auth } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from('events')
    .insert({
      name: input.name.trim(),
      code: (input.code ?? generateEventCode()).trim(),
      location: input.location?.trim() || null,
      color: input.color ?? null,
      start_at: toIso(input.startAt ?? Date.now()) ?? new Date().toISOString(),
      end_at: toIso(input.endAt),
      created_by: auth.user?.id ?? null,
    })
    .select('id')
    .single();
  if (error) {
    if (error.code === '23505') throw new Error(`Kode event "${input.code}" sudah dipakai`);
    throw new Error(error.message);
  }
  return data.id;
}

export interface UpdateEventInput {
  name?: string;
  location?: string | null;
  color?: string | null;
  startAt?: number;
  endAt?: number | null;
}

export async function updateEvent(id: string, input: UpdateEventInput): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) patch.name = input.name.trim();
  if (input.location !== undefined) patch.location = input.location || null;
  if (input.color !== undefined) patch.color = input.color || null;
  if (input.startAt !== undefined) patch.start_at = toIso(input.startAt);
  if (input.endAt !== undefined) patch.end_at = toIso(input.endAt);
  const { error } = await supabase.from('events').update(patch as never).eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Close an event and record what came back to the warehouse.
 *
 * `returns` maps ALLOCATION id → qty returned; the gap against qty_in is
 * shrinkage. Keyed by allocation rather than product because one product can be
 * carried in several lots and each is counted back separately — close_event()
 * refuses a product-keyed entry the moment it is ambiguous.
 */
export async function closeEvent(
  eventId: string,
  returns: Record<string, number> = {},
): Promise<void> {
  const { error } = await supabase.rpc('close_event', {
    p_event_id: eventId,
    p_returns: Object.entries(returns).map(([allocation_id, qty]) => ({ allocation_id, qty })),
  });
  if (error) throw new Error(error.message);
}

/**
 * Undo a close. The allocations are reopened too — create_transaction() checks
 * each allocation's own closed_at, not the event's status, so an event that was
 * "active" with closed allocations would refuse every line.
 */
export async function reopenEvent(id: string): Promise<void> {
  const { error } = await supabase.rpc('reopen_event', { p_event_id: id });
  if (error) throw new Error(error.message);
}

/** Reversible hide. Allowed from active or closed. */
export async function archiveEvent(id: string): Promise<void> {
  const { error } = await supabase.rpc('archive_event', { p_event_id: id });
  if (error) throw new Error(error.message);
}

/** Comes back as active, whatever it was before being archived. */
export async function unarchiveEvent(id: string): Promise<void> {
  const { error } = await supabase.rpc('unarchive_event', { p_event_id: id });
  if (error) throw new Error(error.message);
}

/**
 * One-way. Refused by the database once the event has any transaction, or any
 * allocation showing units sold.
 */
export async function voidEvent(id: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc('void_event', {
    p_event_id: id,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function deleteEvent(id: string): Promise<void> {
  const { error } = await supabase.from('events').delete().eq('id', id);
  if (error) {
    // FK restrict from transactions — deleting would orphan invoices.
    if (error.code === '23503') {
      throw new Error('Event punya transaksi — tidak bisa dihapus. Tutup event saja.');
    }
    throw new Error(error.message);
  }
}

// ==== Reporting ====

export interface TopProduct {
  productId: string;
  name: string;
  qty: number;
  subtotal: number;
}

export interface EventSummary {
  event: AppEvent;
  totalSales: number;
  totalTransactions: number;
  cancelledCount: number;
  byPaymentMethod: Record<string, number>;
  byCashier: Record<string, { name: string; total: number; count: number }>;
  topProducts: TopProduct[];
  outstanding: number;
}

/**
 * Per-event report. Aggregated client-side over the event's transactions —
 * fine at event scale (hundreds of rows). If an event ever grows to tens of
 * thousands, move this into a SQL view; the shape here is already what a view
 * would return.
 */
export async function getEventSummary(eventId: string): Promise<EventSummary> {
  const { data, error } = await supabase
    .from('transactions_view')
    .select('*, transaction_items(*)')
    .eq('event_id', eventId);
  if (error) throw new Error(error.message);

  const event = await getEvent(eventId);
  if (!event) throw new Error('Event tidak ditemukan');

  const rows = data ?? [];
  const summary: EventSummary = {
    event,
    totalSales: 0,
    totalTransactions: 0,
    cancelledCount: 0,
    byPaymentMethod: {},
    byCashier: {},
    topProducts: [],
    outstanding: 0,
  };
  const products = new Map<string, TopProduct>();

  for (const r of rows as never[]) {
    const t = r as {
      payment_status: string; grand_total: number; outstanding_amount: number;
      payment_method: string; cashier_id: string | null; cashier_name: string | null;
      transaction_items: { product_id: string | null; name: string; qty: number; returned_qty: number; price: number }[];
    };
    if (t.payment_status === 'cancelled') {
      summary.cancelledCount += 1;
      continue;
    }

    summary.totalSales += t.grand_total;
    summary.totalTransactions += 1;
    // A returned invoice keeps a non-zero outstanding_amount — create_return()
    // cannot zero it without rewriting the invoice (see the note on
    // reportableOutstanding in reports.ts). Nothing is owed on it, so it must
    // not count here either. 'cancelled' already left the loop above.
    // 'partially_returned' is deliberately still counted: the customer kept
    // some of the goods and owes for them.
    if (t.payment_status !== 'returned') {
      summary.outstanding += t.outstanding_amount;
    }
    summary.byPaymentMethod[t.payment_method] =
      (summary.byPaymentMethod[t.payment_method] ?? 0) + t.grand_total;

    const cid = t.cashier_id ?? 'unknown';
    const c = summary.byCashier[cid] ?? { name: t.cashier_name ?? '—', total: 0, count: 0 };
    c.total += t.grand_total;
    c.count += 1;
    summary.byCashier[cid] = c;

    for (const li of t.transaction_items ?? []) {
      const key = li.product_id ?? li.name;
      // Net of returns: a returned unit was not really sold.
      const netQty = li.qty - li.returned_qty;
      if (netQty <= 0) continue;
      const p = products.get(key) ?? { productId: key, name: li.name, qty: 0, subtotal: 0 };
      p.qty += netQty;
      p.subtotal += netQty * li.price;
      products.set(key, p);
    }
  }

  summary.topProducts = [...products.values()].sort((a, b) => b.qty - a.qty).slice(0, 10);
  return summary;
}
